import { useEffect, useRef, useState } from "react";
import api from "../../api/axios";
import "./ApiDocsPage.css";

const ELEMENTS_SCRIPT =
  "https://unpkg.com/@stoplight/elements@8.4.2/web-components.min.js";
const ELEMENTS_STYLES =
  "https://unpkg.com/@stoplight/elements@8.4.2/styles.min.css";
const INVALID_OPENAPI_MESSAGE =
  "The API returned an invalid OpenAPI document. Please contact support.";

export const toOpenApiJson = (data) => {
  let document = data;

  if (typeof document === "string") {
    try {
      document = JSON.parse(document);
    } catch {
      return null;
    }
  }

  if (
    !document ||
    typeof document !== "object" ||
    Array.isArray(document) ||
    typeof document.openapi !== "string" ||
    !document.info ||
    typeof document.info !== "object" ||
    (!document.paths && !document.webhooks)
  ) {
    return null;
  }

  return JSON.stringify(document);
};

const loadStylesheet = (href) => {
  if (document.querySelector(`link[href="${href}"]`)) {
    return;
  }
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.customElements?.get("elements-api")) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load API docs viewer"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load API docs viewer"));
    document.head.appendChild(script);
  });

const ApiDocsPage = () => {
  const hostRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;

    const mountDocs = async () => {
      setLoading(true);
      setError("");

      try {
        loadStylesheet(ELEMENTS_STYLES);
        await loadScript(ELEMENTS_SCRIPT);

        const response = await api.get("/docs/api.json");
        if (cancelled) {
          return;
        }

        const openApiJson = toOpenApiJson(response.data);
        if (!openApiJson) {
          throw new Error(INVALID_OPENAPI_MESSAGE);
        }

        if (!host) {
          return;
        }

        host.innerHTML = "";
        const docs = document.createElement("elements-api");
        docs.setAttribute("router", "memory");
        docs.setAttribute("layout", "responsive");
        docs.setAttribute("hideTryIt", "true");
        docs.apiDescriptionDocument = openApiJson;
        host.appendChild(docs);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            setError("You do not have permission to view API documentation.");
          } else if (err?.message === INVALID_OPENAPI_MESSAGE) {
            setError(INVALID_OPENAPI_MESSAGE);
          } else {
            setError("Failed to load API documentation.");
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    mountDocs();

    return () => {
      cancelled = true;
      if (host) {
        host.innerHTML = "";
      }
    };
  }, []);

  return (
    <div className="api-docs-page">
      <div className="api-docs-header">
        <h2>API Documentation</h2>
        <p className="text-muted mb-0">
          OpenAPI reference for administrators only.
        </p>
      </div>

      {loading && <div className="api-docs-status">Loading API docs...</div>}
      {error && <div className="api-docs-status api-docs-error">{error}</div>}
      <div
        ref={hostRef}
        className="api-docs-viewer"
        hidden={loading || Boolean(error)}
      />
    </div>
  );
};

export default ApiDocsPage;
